import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { PageList } from "../../components/pageList";
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import moment from "moment";
import "moment/locale/zh-tw";
import { useServerApi } from "../../hooks/userServerApi";
import FiscalPeriodSelect from "../../components/accounting/FiscalPeriodSelect";
import { notifyListRefresh } from "../../context/tabContext";

const apiEntity = "journalEntry";

const formatAmount = (value) =>
  value == null || value === "" ? "" : Number(value).toFixed(2);

const statusColor = (status) => {
  if (status === "POSTED") return "green";
  if (status === "DRAFT") return "yellow";
  return "gray";
};

const buildTableSchema = (t, i18n, onRowOpen) => ({
  showActionColumn: false,
  onRowDoubleClick: ({ data }) => onRowOpen(data),
  columns: [
    {
      field: "code",
      headerName: "Code",
      sortable: true,
    },
    {
      field: "entryDate",
      headerName: "Date",
      sortable: true,
      cellRender: (col, data) =>
        data.entryDate
          ? moment(data.entryDate)
              .locale(i18n.language === "zh-tw" ? "zh-tw" : "en")
              .format("LL")
          : "",
    },
    {
      field: "description",
      headerName: "Description",
      sortable: true,
    },
    {
      field: "sourceType",
      headerName: "accounting.journal.source",
      sortable: true,
      cellRender: (col, data) =>
        data.sourceType
          ? `${data.sourceType}${data.eventType ? ` / ${data.eventType}` : ""}`
          : "",
    },
    {
      field: "totalDebit",
      headerName: "Debit",
      sortable: true,
      cellRender: (col, data) => formatAmount(data.totalDebit),
    },
    {
      field: "totalCredit",
      headerName: "Credit",
      sortable: true,
      cellRender: (col, data) => formatAmount(data.totalCredit),
    },
    {
      field: "status",
      headerName: "Status",
      sortable: true,
      cellRender: (col, data) => (
        <Badge color={statusColor(data.status)}>
          {t(`accounting.journal.status.${data.status}`, data.status)}
        </Badge>
      ),
    },
  ],
  searchableFields: ["code", "description", "sourceType", "eventType"],
});

const emptyLine = () => ({
  accountCode: "",
  accountName: "",
  description: "",
  debit: 0,
  credit: 0,
});

const JournalEntries = () => {
  const { t, i18n } = useTranslation();
  const [api] = useServerApi();
  const location = useLocation();
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseReason, setReverseReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [manualForm, setManualForm] = useState({
    description: "",
    fiscalPeriodId: null,
    remark: "",
    lines: [emptyLine(), emptyLine()],
  });

  const refreshList = () => notifyListRefresh(location.pathname);

  const openDetail = async (entry) => {
    const full = await api.getById({ apiEntity, id: entry._id });
    setSelected(full);
    setDetailOpen(true);
  };

  const tableSchema = useMemo(
    () => buildTableSchema(t, i18n, openDetail),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  const customActions = useMemo(
    () => [
      {
        label: t("accounting.journal.postManual"),
        requiresWrite: true,
        onClick: () => {
          setManualForm({
            description: "",
            fiscalPeriodId: null,
            remark: "",
            lines: [emptyLine(), emptyLine()],
          });
          setManualOpen(true);
        },
      },
    ],
    [t]
  );

  const loadAccounts = async () => {
    try {
      const data = await api.Accounting.getChartOfAccountsTree();
      const accountsList = Array.isArray(data) ? data : data?.accounts || [];
      setAccounts(
        accountsList
          .filter((account) => !account.isControlAccount)
          .map((account) => ({
            value: account.code,
            label: `${account.code} ${account.name}`,
          }))
      );
    } catch (error) {
      showNotification({
        color: "red",
        title: t("accounting.journal.loadFailed"),
        message: String(error?.message || error),
      });
    }
  };

  useEffect(() => {
    if (manualOpen) {
      loadAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualOpen]);

  const activeLines = manualForm.lines.filter(
    (line) => line.accountCode && (line.debit || line.credit)
  );
  const totalDebit = activeLines.reduce(
    (sum, line) => sum + (Number(line.debit) || 0),
    0
  );
  const totalCredit = activeLines.reduce(
    (sum, line) => sum + (Number(line.credit) || 0),
    0
  );
  const isBalanced =
    activeLines.length >= 2 &&
    Number(totalDebit.toFixed(2)) === Number(totalCredit.toFixed(2)) &&
    totalDebit > 0;
  const canPost = Boolean(manualForm.fiscalPeriodId) && isBalanced;

  const handleReverse = async () => {
    if (!selected?._id) return;
    setLoading(true);
    try {
      await api.Accounting.reverseJournal(selected._id, reverseReason);
      showNotification({
        title: t("accounting.journal.reversedTitle"),
        color: "green",
      });
      setReverseOpen(false);
      setDetailOpen(false);
      setReverseReason("");
      refreshList();
    } catch (error) {
      showNotification({
        title: t("accounting.journal.reverseFailed"),
        message: error?.message || t("accounting.journal.reverseFailed"),
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateManualLine = (index, field, value) => {
    setManualForm((prev) => {
      const lines = [...prev.lines];
      lines[index] = { ...lines[index], [field]: value };
      if (field === "accountCode") {
        const account = accounts.find((a) => a.value === value);
        lines[index].accountName = account?.label?.replace(/^\S+\s/, "") || "";
      }
      return { ...prev, lines };
    });
  };

  const handlePostManual = async () => {
    if (!manualForm.fiscalPeriodId) {
      showNotification({
        title: t("accounting.journal.postFailed"),
        message: t("accounting.journal.fiscalPeriodRequired"),
        color: "red",
      });
      return;
    }

    const lines = manualForm.lines
      .filter((line) => line.accountCode && (line.debit || line.credit))
      .map((line) => ({
        accountCode: line.accountCode,
        accountName: line.accountName,
        description: line.description,
        debit: Number(line.debit) || 0,
        credit: Number(line.credit) || 0,
      }));

    if (lines.length < 2) {
      showNotification({
        title: t("accounting.journal.postFailed"),
        message: t("accounting.journal.linesRequired"),
        color: "red",
      });
      return;
    }

    const totalDebit = lines.reduce((sum, line) => sum + line.debit, 0);
    const totalCredit = lines.reduce((sum, line) => sum + line.credit, 0);
    if (Number(totalDebit.toFixed(2)) !== Number(totalCredit.toFixed(2))) {
      showNotification({
        title: t("accounting.journal.postFailed"),
        message: t("accounting.journal.balancedRequired"),
        color: "red",
      });
      return;
    }

    setLoading(true);
    try {
      await api.Accounting.postManualJournal({
        description: manualForm.description,
        fiscalPeriodId: manualForm.fiscalPeriodId,
        remark: manualForm.remark,
        lines,
      });
      showNotification({
        title: t("accounting.journal.postSuccessTitle"),
        color: "green",
      });
      setManualOpen(false);
      setManualForm({
        description: "",
        fiscalPeriodId: null,
        remark: "",
        lines: [emptyLine(), emptyLine()],
      });
      refreshList();
    } catch (error) {
      showNotification({
        title: t("accounting.journal.postFailed"),
        message: error?.message || t("accounting.journal.postFailed"),
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const dateLocale = i18n.language === "zh-tw" ? "zh-tw" : "en";

  return (
    <>
      <PageList
        title={t("accounting.modules.journalEntries.label")}
        apiEntity={apiEntity}
        tableSchema={tableSchema}
        formSchema={{ apiEntity, layout: { fields: [] } }}
        showAddButton={false}
        customActions={customActions}
        initSort={{ by: "entryDate", order: "desc" }}
        defaultPageSize={50}
      />

      <Modal
        opened={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={selected?.code || t("accounting.journal.detailTitle")}
        size="lg"
      >
        {selected && (
          <>
            <Text size="sm" mb="sm">
              {moment(selected.entryDate).locale(dateLocale).format("LL")} —{" "}
              {selected.description}
            </Text>
            <Table striped withBorder mb="md">
              <thead>
                <tr>
                  <th>{t("accounting.journal.account")}</th>
                  <th>{t("Description")}</th>
                  <th>{t("Debit")}</th>
                  <th>{t("Credit")}</th>
                </tr>
              </thead>
              <tbody>
                {(selected.lines || []).map((line, index) => (
                  <tr key={`${line.accountCode}-${index}`}>
                    <td>
                      {line.accountCode} {line.accountName}
                    </td>
                    <td>{line.description}</td>
                    <td>{formatAmount(line.debit)}</td>
                    <td>{formatAmount(line.credit)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {selected.status === "POSTED" && (
              <Button color="red" mt="md" onClick={() => setReverseOpen(true)}>
                {t("accounting.journal.reverse")}
              </Button>
            )}
          </>
        )}
      </Modal>

      <Modal
        opened={reverseOpen}
        onClose={() => setReverseOpen(false)}
        title={t("accounting.journal.reverseTitle")}
      >
        <Textarea
          label={t("accounting.journal.reason")}
          value={reverseReason}
          onChange={(e) => setReverseReason(e.target.value)}
          mb="md"
        />
        <Button color="red" loading={loading} onClick={handleReverse}>
          {t("accounting.journal.reverseConfirm")}
        </Button>
      </Modal>

      <Modal
        opened={manualOpen}
        onClose={() => setManualOpen(false)}
        title={t("accounting.journal.postManualTitle")}
        size="xl"
        keepMounted={false}
      >
        <TextInput
          label={t("Description")}
          value={manualForm.description}
          onChange={(e) => {
            const description = e.currentTarget.value;
            setManualForm((prev) => ({ ...prev, description }));
          }}
          mb="sm"
        />
        {manualOpen && (
          <FiscalPeriodSelect
            value={manualForm.fiscalPeriodId}
            onChange={(value) =>
              setManualForm((prev) => ({ ...prev, fiscalPeriodId: value }))
            }
            clearable={false}
          />
        )}
        {accounts.length === 0 && (
          <Alert color="yellow" mb="md">
            {t("accounting.journal.noAccounts")}
          </Alert>
        )}
        <Textarea
          label={t("accounting.journal.remark")}
          mt="sm"
          mb="md"
          value={manualForm.remark}
          onChange={(e) => {
            const remark = e.currentTarget.value;
            setManualForm((prev) => ({ ...prev, remark }));
          }}
        />
        {manualForm.lines.map((line, index) => (
          <Group key={`line-${index}`} grow mb="xs" align="flex-end">
            <Select
              label={`${t("accounting.journal.account")} ${index + 1}`}
              data={accounts}
              searchable
              value={line.accountCode || null}
              onChange={(value) => updateManualLine(index, "accountCode", value)}
            />
            <NumberInput
              label={t("Debit")}
              precision={2}
              value={line.debit}
              onChange={(value) => updateManualLine(index, "debit", value)}
            />
            <NumberInput
              label={t("Credit")}
              precision={2}
              value={line.credit}
              onChange={(value) => updateManualLine(index, "credit", value)}
            />
          </Group>
        ))}
        <Group position="apart" mt="md" mb="xs">
          <Text size="sm" weight={600}>
            {t("accounting.journal.totals", {
              debit: totalDebit.toFixed(2),
              credit: totalCredit.toFixed(2),
            })}
          </Text>
          <Badge color={isBalanced ? "green" : "red"}>
            {isBalanced
              ? t("accounting.trialBalance.balanced")
              : t("accounting.trialBalance.outOfBalance")}
          </Badge>
        </Group>
        <Group mt="md">
          <Button
            variant="default"
            onClick={() =>
              setManualForm((prev) => ({
                ...prev,
                lines: [...prev.lines, emptyLine()],
              }))
            }
          >
            {t("accounting.journal.addLine")}
          </Button>
          <Button loading={loading} disabled={!canPost} onClick={handlePostManual}>
            {t("accounting.journal.postJournal")}
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default JournalEntries;
